import hitTemplate from './algolia-custom-hit.js';
//import { infiniteHits } from './algolia-custom-infinite-hits.js';
import { initAlgoliaMobileDrawer } from './algolia-custom-drawer.js';
import { initRefinementListFade } from './algolia-refinement-list-fade.js';
import {
  facetStateSanitizeWidget,
  customerPreferencesWidget,
} from './algolia-my-size.js';
import {
  stripSuggestedRefinementsFromUrl,
  enhanceFacetParams,
  suggestedRefinementWidget,
} from './algolia-suggested-refinement.js';

if (typeof window !== 'undefined') {
  stripSuggestedRefinementsFromUrl();
}

const setSearchInputPlaceholder = () => {
  const searchInput = document.querySelector('#algolia-search-form .aa-Input');
  if (searchInput) {
    searchInput.placeholder = 'Search';
  }
  if (searchInput && !searchInput.classList.contains('blur-init')) {
    searchInput.addEventListener('blur', () => {
      searchInput.classList.add('blur-init');
      searchInput.placeholder = 'Search';
    });
  }
};
window.addEventListener('load', () => {
  setSearchInputPlaceholder();
  const algoliaForm = document.querySelector('#algolia-search-form');
  if (algoliaForm) {
    algoliaForm.addEventListener('change', (e) => {
      setSearchInputPlaceholder();
    });
  }
});
window.addEventListener('resize', () => {
  setSearchInputPlaceholder();
});
function simplifyProductTypeFacetLabels() {
  document.querySelectorAll('.ais-facet-product_type .ais-RefinementList-label')?.forEach(labelEl => {
    if (labelEl?.childNodes) {
      const fullTextNode = Array.from(labelEl.childNodes).find(node => node.nodeType === 3);
      if (fullTextNode) {
        const parts = fullTextNode.textContent.split('>').map(p => p.trim());
        if (parts.length >= 2) {
          fullTextNode.textContent = `${parts[0]} ${parts[parts.length - 1]}`;
        } else {
          fullTextNode.textContent = parts[0];
        }
      }
    }
  });
}

function observeProductTypeChanges() {
  const target = document.querySelector('.ais-facet-product_type');

  if (!target) return;

  const observer = new MutationObserver(() => {
    simplifyProductTypeFacetLabels();
  });

  observer.observe(target, {
    childList: true,
    subtree: true
  });
}

function injectLoadingOverlay() {
  if (document.getElementById('algolia-loading-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'algolia-loading-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    background: rgba(255, 255, 255, 0.9);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  spinner.style.cssText = `
    width: 40px;
    height: 40px;
    border: 4px solid #ccc;
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  overlay.appendChild(spinner);
  document.head.appendChild(style);
  document.body.appendChild(overlay);
}

function showLoadingOverlay() {
  const overlay = document.getElementById('algolia-loading-overlay');
  if (overlay) overlay.style.display = 'flex';
}

function hideLoadingOverlay() {
  const overlay = document.getElementById('algolia-loading-overlay');
  if (overlay) overlay.style.display = 'none';
}

function enhanceAlgoliaFacetsWithIcons() {
  document.querySelectorAll('.ais-facet-dropdown-wrapper').forEach(wrapper => {
    const isVendor = wrapper.classList.contains('ais-facet-vendor');
    const checkbox = wrapper.querySelector('.ais-dropdown-checkbox');
    const label = wrapper.querySelector('.ais-facet--header');
    const container = wrapper.querySelector('.ais-facet-dropdown-container');

    if (!checkbox || !label || !container) return;

    checkbox.checked = isVendor;

    let icon = label.querySelector('.facet-toggle-icon');
    if (!icon) {
      icon = document.createElement('span');
      icon.className = 'facet-toggle-icon';
      label.appendChild(icon);
    }

    const updateAccordion = () => {
      const isOpen = checkbox.checked;
      icon.textContent = isOpen ? '−' : '+';
      container.style.display = isOpen ? 'block' : 'none';
    };

    updateAccordion();

    checkbox.removeEventListener('change', updateAccordion);
    checkbox.addEventListener('change', updateAccordion);
  });
}

// Minimum characters before Algolia runs a search. Shared by both forms.
const MIN_QUERY_LENGTH = 3;

export function initAlgolia(showSpinner = true) {
    // ---- Header / home page Autocomplete (#algolia-search-form) ----
    // Registered BEFORE the InstantSearch early-return below so it also runs on pages
    // without InstantSearch (e.g. the home page). The as-you-type panel is built by the
    // Algolia Shopify app via @algolia/autocomplete-js.
    const MIN_CHARS_MESSAGE = 'Start typing! Enter 3 or more characters to find your next snag.';
    const queryLength = (query) => (query || '').trim().length;

    // 1) Stop keystroke requests under the minimum. Returning [] from every source means
    //    autocomplete-js sends NO request. The panel has a main getSources (Products /
    //    Collections / Pages) and separate plugins (Query Suggestions) — guard both.
    //    Form submission is unaffected (native GET to ?q=...).
    algoliaShopify.hooks.registerHook(
        'beforeAutocompleteOptions',
        function (options) {
            const originalGetSources = options.getSources;
            if (typeof originalGetSources === 'function') {
                options.getSources = function (params) {
                    if (queryLength(params && params.query) < MIN_QUERY_LENGTH) return [];
                    return originalGetSources.call(this, params);
                };
            }
            if (Array.isArray(options.plugins)) {
                options.plugins = options.plugins.map((plugin) => {
                    if (plugin && typeof plugin.getSources === 'function') {
                        const pluginGetSources = plugin.getSources;
                        return {
                            ...plugin,
                            getSources(params) {
                                if (queryLength(params && params.query) < MIN_QUERY_LENGTH) return [];
                                return pluginGetSources.call(plugin, params);
                            },
                        };
                    }
                    return plugin;
                });
            }
            return options;
        }
    );

    // 2) With no sources the app renders its own "No results for '<query>'" panel, which we
    //    can't reach through a hook. Rather than fight the app's renderer (it re-adds its own
    //    content), we keep OUR hint as a separate node we own, and toggle a class on the panel
    //    that hides the app's content ONLY while the query is 1-2 characters. All operations
    //    are idempotent so there is no observer feedback loop. IMPORTANT: the hint state is
    //    driven by the actual query length (below the minimum), NOT by whether results exist —
    //    a 3+ character query with genuinely no results must show the app's "No results", not
    //    our hint. We re-sync on two signals: keystrokes (input event, reliable per character)
    //    and panel re-renders (MutationObserver, for async result rendering).
    if (!window.__minCharsPanelObserver) {
        const HINT_CLASS = 'aa-min-chars-hint';
        const syncMinCharsPanel = () => {
            const input = document.querySelector(
                '#algolia-search-form .aa-Input, .aa-DetachedOverlay .aa-Input, .aa-Input'
            );
            const len = queryLength(input && input.value);
            const shouldShow = len > 0 && len < MIN_QUERY_LENGTH; // 1-2 chars only
            document.querySelectorAll('.aa-Panel').forEach((panel) => {
                let hint = panel.querySelector(':scope > .' + HINT_CLASS);
                if (shouldShow) {
                    if (!hint) {
                        hint = document.createElement('div');
                        hint.className = HINT_CLASS + ' aa-min-chars-message';
                        hint.textContent = MIN_CHARS_MESSAGE;
                        panel.insertBefore(hint, panel.firstChild);
                    }
                    if (!panel.classList.contains('has-min-chars')) {
                        panel.classList.add('has-min-chars');
                    }
                } else {
                    if (hint) hint.remove();
                    if (panel.classList.contains('has-min-chars')) {
                        panel.classList.remove('has-min-chars');
                    }
                }
            });
        };
        // Keystrokes: fires on every character in the autocomplete input, even when the panel
        // only changes text (which the childList observer below would miss). rAF lets the app
        // render first so any freshly-created panel is present when we sync.
        document.addEventListener('input', (e) => {
            if (e.target && e.target.classList && e.target.classList.contains('aa-Input')) {
                requestAnimationFrame(syncMinCharsPanel);
            }
        }, true);
        // Panel re-renders: catches async result/no-result rendering after a request returns.
        const observer = new MutationObserver(syncMinCharsPanel);
        observer.observe(document.body, { childList: true, subtree: true });
        window.__minCharsPanelObserver = observer;
    }

    const AlgoliaInstantSearchInstance = document.getElementById('algolia-shopify-instantsearch');
    if (!AlgoliaInstantSearchInstance) {
        return;
    }

    // ---- InstantSearch (search results AND Algolia-powered collection pages) ----
    // We only want to suppress a genuine as-you-type search of 1-2 characters. Everything else
    // must behave exactly as the app intends — importantly, collection pages run with an EMPTY
    // query and rely on the app's own searchFunction (rule contexts / collection scoping). So
    // we WRAP the app's existing searchFunction instead of replacing it: empty queries, the
    // submitted ?q= query, and 3+ char queries all fall through to the original untouched;
    // only a 1-2 char typed query is skipped.
    const submittedQuery = (new URLSearchParams(window.location.search).get('q') || '').trim();
    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchConfigurationOptions',
        function (options) {
            const originalSearchFunction = options.searchFunction;
            const runSearch = function (helper) {
                if (typeof originalSearchFunction === 'function') {
                    originalSearchFunction.call(this, helper);
                } else {
                    helper.search();
                }
            };
            options.searchFunction = function (helper) {
                const query = (helper.state.query || '').trim();
                // Skip ONLY a partial typed query (1-2 chars) that isn't the submitted one.
                if (query.length > 0 && query.length < MIN_QUERY_LENGTH && query !== submittedQuery) {
                    return;
                }
                runSearch.call(this, helper);
            };
            return options;
        }
    );

    if (showSpinner) {
        injectLoadingOverlay();
        showLoadingOverlay();
    }

    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchProductTemplate',
        hitTemplate
    );

    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchStatsTemplate',
        function (defaultTemplate, data, html) {
            return `
            <strong>${algoliaShopify.helpers.formatNumber(data.nbHits)}</strong> result${data.nbHits !== 1 ? 's' : ''} found
            `;
        }
    );

    algoliaShopify.hooks.registerHook('beforeInstantSearchStartAddWidgets', function() {
        // Widget to initialize autocomplete on InstantSearch search input
        const autocompleteWidget = {
            $$type: 'custom.initAutocomplete',
            
            init: function({ instantSearchInstance }) {
                // Store instantSearchInstance for later use
                this.instantSearchInstance = instantSearchInstance;
                
                // Initialize autocomplete on the search input
                const initAutocomplete = () => {
                    const searchInput = document.querySelector('.ais-SearchBox-input');
                    if (!searchInput) {
                        setTimeout(initAutocomplete, 100);
                        return;
                    }
                    
                    // Check if already initialized
                    if (searchInput.hasAttribute('data-autocomplete-initialized')) return;
                    
                    // Wait for Algolia Shopify app to be ready
                    if (typeof window.algoliaShopify === 'undefined' || 
                        !window.algoliaShopify.externals || 
                        !window.algoliaShopify.externals.autocomplete) {
                        setTimeout(initAutocomplete, 100);
                        return;
                    }
                    
                    const { autocomplete, getAlgoliaResults } = window.algoliaShopify.externals;
                    
                    // Get search client and index name from instantSearchInstance
                    const searchClient = instantSearchInstance.client;
                    const indexName = instantSearchInstance.helper.state.index;
                    
                    if (!searchClient || !indexName) {
                        setTimeout(initAutocomplete, 100);
                        return;
                    }
                    
                    try {
                        // Create autocomplete instance
                        const autocompleteInstance = autocomplete({
                            container: searchInput,
                            placeholder: 'Search',
                            openOnFocus: true,
                            getSources({ query }) {
                                if (!query) return [];
                                
                                return [
                                    {
                                        sourceId: 'products',
                                        getItems() {
                                            return getAlgoliaResults({
                                                searchClient,
                                                queries: [
                                                    {
                                                        indexName: indexName,
                                                        query,
                                                    },
                                                ],
                                            }).then(({ results }) => {
                                                return results[0]?.hits || [];
                                            });
                                        },
                                        templates: {
                                            item({ item, components }) {
                                                // Use the same hit template as InstantSearch
                                                return hitTemplate({ item, components });
                                            },
                                        },
                                    },
                                ];
                            },
                            onStateChange({ state }) {
                                // Sync autocomplete query with InstantSearch when user selects an item
                                if (state.isOpen === false && state.query) {
                                    instantSearchInstance.helper.setQuery(state.query).search();
                                }
                            },
                        });
                        
                        // Store autocomplete instance for cleanup if needed
                        searchInput.autocompleteInstance = autocompleteInstance;
                        searchInput.setAttribute('data-autocomplete-initialized', 'true');
                        console.log('Autocomplete initialized on InstantSearch input');
                    } catch (error) {
                        console.error('Error initializing autocomplete:', error);
                    }
                };
                
                // Initialize after a short delay to ensure DOM is ready
                setTimeout(initAutocomplete, 200);
            },
            
            render: function({ results }) {
                // Re-initialize if search input appears later
                const searchInput = document.querySelector('.ais-SearchBox-input');
                if (searchInput && !searchInput.hasAttribute('data-autocomplete-initialized')) {
                    this.init({ instantSearchInstance: this.instantSearchInstance });
                }
            }
        };
        
        const revealWidget = {
            $$type: 'custom.revealOnLoad',
            
            render: function({ results }) {
                if (results && results.nbHits !== undefined) {
                    AlgoliaInstantSearchInstance?.classList.add('algolia-ready');
                    hideLoadingOverlay();
                }
            }
        };

        // Custom infinite scroll widget
        const infiniteScrollWidget = {
            $$type: 'custom.infiniteScroll',
            
            init: function({ helper, instantSearchInstance }) {
                this.helper = helper;
                this.allHits = [];
                this.isLoading = false;
                this.observer = null;
            },
            
            render: function({ results }) {
                if (!results) return;
                
                // Find hits container
                const hitsContainer = document.querySelector('.ais-Hits-list') || 
                                    document.querySelector('[data-widget="hits"]') ||
                                    document.querySelector('.ais-Hits');
                
                if (!hitsContainer) return;
                
                const isLastPage = results.page >= results.nbPages - 1;
                
                // Accumulate hits
                if (results.page === 0) {
                    this.allHits = results.hits;
                } else {
                    // Only add new hits that aren't already in the list
                    const existingIds = new Set(this.allHits.map(h => h.objectID));
                    const newHits = results.hits.filter(h => !existingIds.has(h.objectID));
                    this.allHits = this.allHits.concat(newHits);
                }
                
                // Hide default pagination
                const pagination = document.querySelector('.ais-Pagination');
                if (pagination) {
                    pagination.style.display = 'none';
                }
                
                // Setup intersection observer for infinite scroll
                if (!this.observer && !isLastPage) {
                    let sentinel = document.querySelector('.infinite-scroll-sentinel');
                    
                    if (!sentinel) {
                        sentinel = document.createElement('div');
                        sentinel.className = 'infinite-scroll-sentinel';
                        sentinel.style.height = '1px';
                        sentinel.style.visibility = 'hidden';
                        
                        // Insert after hits container
                        hitsContainer.parentElement.insertBefore(sentinel, hitsContainer.nextSibling);
                    }
                    
                    this.observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting && !this.isLoading && !isLastPage) {
                                this.isLoading = true;
                                this.helper.setPage(results.page + 1).search();
                                setTimeout(() => {
                                    this.isLoading = false;
                                }, 1000);
                            }
                        });
                    }, {
                        rootMargin: '300px'
                    });
                    
                    this.observer.observe(sentinel);
                    this.sentinel = sentinel;
                }
                
                // Remove sentinel on last page
                if (isLastPage && this.sentinel) {
                    if (this.observer) {
                        this.observer.disconnect();
                    }
                    this.sentinel.remove();
                }
            }
        };

        return [
            facetStateSanitizeWidget,
            suggestedRefinementWidget,
            customerPreferencesWidget,
            autocompleteWidget,
            revealWidget,
            //infiniteScrollWidget
        ];
    });

    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchFacetParamsOptions',
        function (params, facet) {
            return enhanceFacetParams(params, facet);
        }
    );

    setTimeout(() => {
        enhanceAlgoliaFacetsWithIcons();
        initAlgoliaMobileDrawer();
        observeProductTypeChanges();
        initRefinementListFade();
    }, 600);
}

initRefinementListFade();

document.addEventListener('algolia.hooks.initialize', initAlgolia);